import { type FullOptions, Searcher } from "fast-fuzzy"
import { useEffect, useMemo, useRef, useState } from "react"

const DEFAULT_RESULTS_COUNT = 20

// Specifically for filtering Select options with `label` prop
const useOptimizedFilter = <T extends { label: string; id?: string | number }>(
  items: T[],
  resultsCount = DEFAULT_RESULTS_COUNT
) => {
  const [searchTerm, setSearchTerm] = useState<string>("")
  // `returnMatchData` is never set, so `search()` yields `T[]`; typing the
  // options as `FullOptions<T>` (not `any`) lets TS resolve that overload.
  const searcherRef = useRef<Searcher<T, FullOptions<T>> | null>(null)
  const itemsMapRef = useRef<Map<string | number, T>>(new Map())

  // Initialize or update searcher when items change
  useEffect(() => {
    const currentMap = itemsMapRef.current
    const newMap = new Map<string | number, T>()

    // Build new map for comparison
    items.forEach((item) => {
      // Use item.id if available, otherwise use item.label as identifier
      const key = item.id ?? item.label
      newMap.set(key, item)
    })

    // If searcher doesn't exist, create it
    if (!searcherRef.current) {
      searcherRef.current = new Searcher(items, {
        threshold: 0.9,
        ignoreCase: true,
        ignoreSymbols: false,
        keySelector: (item: T) => item.label,
      })
      itemsMapRef.current = newMap
      return
    }

    // Find items to add (exist in new but not in current)
    const itemsToAdd: T[] = []
    for (const [key, item] of newMap) {
      if (!currentMap.has(key)) {
        itemsToAdd.push(item)
      }
    }

    // Add new items incrementally
    if (itemsToAdd.length > 0) {
      searcherRef.current.add(...itemsToAdd)
    }

    // Note: fast-fuzzy doesn't have a remove method, so if you need to handle
    // item removal, you'd need to rebuild the searcher
    const removedItems = Array.from(currentMap.keys()).filter(
      (key) => !newMap.has(key)
    )
    if (removedItems.length > 0) {
      // Rebuild searcher when items are removed
      searcherRef.current = new Searcher(items, {
        threshold: 0.9,
        ignoreCase: true,
        ignoreSymbols: false,
        keySelector: (item: T) => item.label,
      })
    }

    itemsMapRef.current = newMap
  }, [items])

  const filteredItems = useMemo(() => {
    if (!searcherRef.current) return items.slice(0, resultsCount)
    if (!searchTerm) return items.slice(0, resultsCount)

    const results = searcherRef.current.search(searchTerm)
    return results.slice(0, resultsCount)
  }, [searchTerm, resultsCount, items])

  return { filteredItems, onSearch: setSearchTerm }
}

export default useOptimizedFilter
